<script setup lang="ts">
import { VdFormField, VdForm } from "@voodoo.io/voodoo-ui";
import { reactive, computed, watch, PropType } from "vue";

import { useLayerSaveMutation, useLayerQuery, useMutationResponse, LayerInput } from "@/graphql";

const props = defineProps({
  visible: {
    type: Boolean,
    required: false,
    default: false,
  },
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  layerId: {
    type: String,
    required: false,
    default: null,
  },
  layersList: {
    type: Array as PropType<string[]>,
    required: true,
    default: () => null,
  },
});

const emit = defineEmits(["close"]);

/**
 * We need
 */
const queryVariables = computed(() => ({
  gameId: props.gameId,
  versionId: props.versionId,
  layerId: props.layerId,
}));

const form = reactive<LayerInput>({
  name: "",
});

const formRules = {
  name: [
    {
      type: "string",
      trigger: "blur",
      validator(_: unknown, value: string, callback: (error?: Error) => void) {
        if (props.layersList.includes(value)) {
          return callback(new Error("The name of the layer must be unique."));
        }
        return callback();
      },
    },
  ],
};

const { result, loading: layerLoading } = useLayerQuery(queryVariables, () => ({
  fetchPolicy: "network-only",
  enabled: !!props.layerId,
}));
const layer = computed(() => result.value?.game?.version?.layer);

watch(
  [() => props.layerId, layer],
  ([watchedLayerIdProp, watchedQueriedLayer]) => {
    form.name = (watchedLayerIdProp && watchedQueriedLayer?.name) || "";
  },
  { immediate: true }
);

const onCloseDialog = (layerId?: string) => {
  form.name = "";
  emit("close", layerId ?? undefined);
};

const { mutate: save, loading: saveLoading } = useLayerSaveMutation({
  refetchQueries: ["AbtestFormPage", "AbtestListPage", "AbtestsLayerSelect"],
});
const onSaveLayer = async () => {
  const response = await save({
    gameId: props.gameId,
    versionId: props.versionId,
    layerId: props.layerId ?? undefined,
    input: form,
  });

  const data = useMutationResponse(response, (resp) => resp?.saveLayer);

  if (!saveLoading.value && data?.success) {
    onCloseDialog(data.layerId);
  }
};
</script>

<template>
  <vd-dialog
    v-loading="layerLoading"
    class="abtest-layer-dialog-component"
    :visible="visible"
    :title="layerId && layer ? layer.name : 'Create a new layer'"
    @close="onCloseDialog"
  >
    <VdForm :form="form" :rules="formRules" @submit="onSaveLayer" @cancel="onCloseDialog">
      <VdFormField
        v-model="form.name"
        prop="name"
        type="text"
        label="Layer name"
        help-text="⚠️ The name of your layer should be unique"
        required
      ></VdFormField>
    </VdForm>
  </vd-dialog>
</template>

<style lang="scss" scoped>
.abtest-layer-dialog-component {
  width: fit-content;

  :deep(.el-dialog) {
    max-width: 500px;
  }

  :deep(.vd-form__content) {
    grid-template-columns: 1fr;
  }
}
</style>
